import zionDebug from '@fs/zion-debug'
import React, { createContext, useContext, useMemo, useReducer } from 'react'

const debug = zionDebug('ly:BrowseProvidersReducer')
const BrowseProviderContext = createContext(null)

/**
 * @typedef {Object} BrowseProviderState
 * @property {boolean} showItemBreadcrumbs - Indicates whether the image group item breadcrumbs are displayed.
 */
export const defaultInitialState = {
  showItemBreadcrumbs: true, // Shows the image group item breadcrumbs.
}

/**
 * Handles all the states
 * @param {BrowseProviderState} State - The current value of the state
 * @param {Object} Action - The value that will be used to determine the next state
 * @return {BrowseProviderState} - The new state
 */
const browseProviderReducer = (state, action) => {
  debug('reducer called: ', state, action.type, action)

  switch (action.type) {
    case 'SET_SHOW_ITEM_BREADCRUMBS': {
      return { ...state, showItemBreadcrumbs: action.value }
    }
    default: {
      throw new Error(`Unhandled type in BrowseProvider reducer: ${action.type}`)
    }
  }
}

/**
 * BrowseProvider component that provides state and dispatch functionality
 * to its children via React Context.
 *
 * @param {Object} props - The props object.
 * @param {React.ReactNode} props.children - The child components to be wrapped by the provider.
 * @param {Partial<BrowseProviderState>} props.initialState - The initial state to be merged with the default state.
 * @returns {JSX.Element} The provider component wrapping its children with context.
 */
const BrowseProvider = ({ children, initialState }) => {
  const [state, dispatch] = useReducer(browseProviderReducer, { ...defaultInitialState, ...initialState })

  const contextValue = useMemo(() => ({ ...state, dispatch }), [state, dispatch])

  return <BrowseProviderContext.Provider value={contextValue}>{children}</BrowseProviderContext.Provider>
}

/**
 * Custom hook to access the BrowseProvider context.
 * Ensures that the hook is used within a BrowseProvider component.
 *
 * @throws {Error} If the hook is used outside of a BrowseProvider.
 * @returns {{dispatch: React.Dispatch<{type: string, value: any}>} & BrowseProviderState} The context value provided by BrowseProvider.
 */
export const useBrowseProviderContext = () => {
  const context = useContext(BrowseProviderContext)

  if (!context) {
    throw new Error('useBrowseProviderContext must be used within a BrowseProvider')
  }

  return context
}

export default BrowseProvider
